(() => {
  // A section can include this asset more than once in the theme editor.
  if (window.qt4uBrandCarousel) {
    window.qt4uBrandCarousel.init(document);
    return;
  }

  const instances = new Map();
  const selector = '[data-brand-carousel]';
  const interval = 4000;
  const sections = (root) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector)
  ];

  const mount = (carousel) => {
    if (instances.has(carousel)) return;
    const viewport = carousel.querySelector('.brand-ribbon__track-wrap');
    const controls = carousel.querySelector('.brand-ribbon__controls');
    const buttons = [...carousel.querySelectorAll('[data-brand-direction]')];
    const toggle = carousel.querySelector('[data-brand-autoplay]');
    if (!viewport || !controls || buttons.length !== 2 || !toggle) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cleanup = [];
    let timer = null;
    let destroyed = false;
    let userPaused = false;
    let hovered = false;
    let focused = carousel.contains(document.activeElement);
    let inView = false;
    let max = 0;

    const listen = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      cleanup.push(() => target.removeEventListener(event, handler, options));
    };
    const stop = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const canPlay = () => !destroyed && !userPaused && !motion.matches &&
      !document.hidden && !hovered && !focused && inView && max > 2;
    const scrollTo = (left) => viewport.scrollTo({
      left: Math.max(0, Math.min(max, left)),
      behavior: motion.matches ? 'auto' : 'smooth'
    });
    const schedule = () => {
      if (!canPlay()) { stop(); return; }
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        if (!canPlay()) return;
        const logo = carousel.querySelector('.brand-logo');
        const step = logo?.getBoundingClientRect().width || Math.min(220, viewport.clientWidth);
        // Rewind the real track gently; never clone links or duplicate tab stops.
        scrollTo(viewport.scrollLeft >= max - 2 ? 0 : viewport.scrollLeft + step);
        schedule();
      }, interval);
    };
    const syncToggle = () => {
      toggle.hidden = motion.matches;
      toggle.textContent = userPaused ? 'Resume' : 'Pause';
      toggle.setAttribute('aria-label', userPaused ? 'Resume automatic brand rotation' : 'Pause automatic brand rotation');
      schedule();
    };
    const update = () => {
      max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      controls.hidden = max <= 2;
      buttons[0].disabled = viewport.scrollLeft <= 2;
      buttons[1].disabled = viewport.scrollLeft >= max - 2;
      schedule();
    };
    const move = (direction) => {
      stop();
      scrollTo(viewport.scrollLeft + direction * Math.max(180, viewport.clientWidth * 0.8));
      schedule();
    };

    buttons.forEach((button) => listen(button, 'click', () => move(Number(button.dataset.brandDirection))));
    listen(toggle, 'click', () => { userPaused = !userPaused; syncToggle(); });
    listen(viewport, 'keydown', (event) => {
      if (event.target !== viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    listen(viewport, 'scroll', update, { passive: true });
    listen(carousel, 'mouseenter', () => { hovered = true; schedule(); });
    listen(carousel, 'mouseleave', () => { hovered = false; schedule(); });
    listen(carousel, 'focusin', () => { focused = true; schedule(); });
    listen(carousel, 'focusout', (event) => {
      focused = !!event.relatedTarget && carousel.contains(event.relatedTarget);
      schedule();
    });
    listen(document, 'visibilitychange', schedule);
    if (motion.addEventListener) listen(motion, 'change', syncToggle);
    else {
      motion.addListener(syncToggle);
      cleanup.push(() => motion.removeListener(syncToggle));
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inView = entries.some((entry) => entry.target === carousel && entry.isIntersecting);
        schedule();
      }, { threshold: 0 });
      observer.observe(carousel);
      cleanup.push(() => observer.disconnect());
    } else {
      const visibility = () => {
        const rect = carousel.getBoundingClientRect();
        inView = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
        schedule();
      };
      listen(window, 'scroll', visibility, { passive: true });
      listen(window, 'resize', visibility);
      visibility();
    }

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      observer.observe(viewport);
      cleanup.push(() => observer.disconnect());
    } else listen(window, 'resize', update);

    carousel.dataset.carouselReady = 'true';
    instances.set(carousel, () => {
      destroyed = true;
      stop();
      cleanup.forEach((dispose) => dispose());
      delete carousel.dataset.carouselReady;
      instances.delete(carousel);
    });
    update();
    syncToggle();
  };
  const init = (root = document) => sections(root).forEach(mount);
  const destroy = (root) => sections(root).forEach((carousel) => instances.get(carousel)?.());
  window.qt4uBrandCarousel = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
